import { memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import debounce from 'lodash/debounce';
import equal from 'deep-equal';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';

import { deleteObjectKeys } from 'utils/deleteObjectKeys';
import { ClientListParams } from 'types/client';
import { CommentListParams } from 'types/comment';
import { CourseListParams } from 'types/course';
import { OrderListParams } from 'types/order';
import { FiltersSearch } from '../FiltersSearch';
import { FilterIcon } from './FilterIcon';
import { useDispatch } from 'redux/store';
import { Filters } from '../Filters';
import { Typography } from '../Typography/Typography';

import s from './styles.module.scss';

export type QueryParams = ClientListParams | OrderListParams | CourseListParams | CommentListParams;

export type SearchableQueryParams = QueryParams & { [K in 'scan' | 'name']?: string };

export interface FiltersControlsProps {
  params: SearchableQueryParams;
  setParams: ActionCreatorWithPayload<QueryParams>;
  filtersListParams?: QueryParams;
  filtersErrors?: Record<string, string>;
  initialParams?: QueryParams;
  searchField?: 'scan' | 'name';
  searchLabel?: string;
  showFilters?: boolean;
  filtersNode?: ReactNode;
  showSelected?: boolean;
  selectedText?: string;
}

const FiltersControls = (props: FiltersControlsProps) => {
  const {
    params,
    setParams,
    filtersListParams,
    filtersErrors,
    initialParams = {},
    searchField = undefined,
    searchLabel = 'Найти',
    filtersNode,
    showFilters = false,
    showSelected = false,
    selectedText = '',
  } = props;

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!searchField) return;
    if (!(searchField in params)) return;

    setSearch(params[searchField] || '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (!searchField) return;

        const newParams = value
          ? { ...params, [searchField]: value }
          : deleteObjectKeys(params, [searchField]);

        dispatch(setParams({ ...newParams, page: 1 }));
      }, 300),
    [dispatch, setParams, params, searchField]
  );

  const paramsWithoutPage = useMemo(() => deleteObjectKeys(params, ['page']), [params]);
  const { per_page: perPage, ...paramsWithoutPerPageAndPage } = paramsWithoutPage;

  const initialParamsWithoutPerPage = useMemo(
    () => deleteObjectKeys(initialParams, ['per_page']),
    [initialParams]
  );
  const initialParamsWithoutPerPageAndPage = useMemo(
    () => deleteObjectKeys(initialParams, ['per_page', 'page']),
    [initialParams]
  );

  const handleReset = useCallback(() => {
    setSearch('');

    dispatch(setParams({ ...initialParamsWithoutPerPage, per_page: perPage }));
  }, [dispatch, initialParamsWithoutPerPage, perPage, setParams]);

  const hasFilters = !equal(paramsWithoutPerPageAndPage, initialParamsWithoutPerPageAndPage, {
    strict: true,
  });

  return (
    <>
      <div className={s.filtersControls}>
        {searchField && (
          <FiltersSearch
            searchLabel={searchLabel}
            search={search}
            setSearch={setSearch}
            onSearch={onSearch}
          />
        )}

        {showFilters && (
          <button className={s.filters} onClick={() => setOpened(!opened)}>
            <FilterAltIcon sx={{ fill: '#1976d2' }} />
            <Typography>
              Фильтры
              {hasFilters && <FilterIcon />}
            </Typography>
          </button>
        )}

        {hasFilters && (
          <button className={s.reset} onClick={handleReset} type="button">
            <ClearIcon sx={{ fill: '#D32F2F' }} />
            <Typography>Сбросить</Typography>
          </button>
        )}

        {showSelected && selectedText && (
          <div className={s.selected}>
            <Typography component="p" sx={{ color: '#00000099' }}>
              Показано:
            </Typography>
            <Typography component="p">{selectedText}</Typography>
          </div>
        )}
      </div>
      {showFilters &&
        createPortal(
          <Filters
            filtersListParams={filtersListParams}
            filtersErrors={filtersErrors}
            setParams={setParams}
            opened={opened}
            setOpened={setOpened}
            onReset={handleReset}
          >
            {filtersNode}
          </Filters>,
          document.body
        )}
    </>
  );
};

export const FiltersControlsMemo = memo(FiltersControls);
